import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownUp, Check } from 'lucide-react';
import type { GroupByKey } from '@/hooks/useGroupedSimulations.ts';

interface GroupBySelectorProps {
  value: GroupByKey;
  onChange: (key: GroupByKey) => void;
  /** When provided, only these keys are shown. Defaults to all options. */
  availableKeys?: GroupByKey[];
}

export default function GroupBySelector({ value, onChange, availableKeys }: GroupBySelectorProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const options: { key: GroupByKey; label: string }[] = useMemo(() => [
    { key: 'none', label: t('groupBy.none') },
    { key: 'simulationType', label: t('groupBy.simulationType') },
    { key: 'salePhase', label: t('groupBy.salePhase') },
    { key: 'sector', label: t('groupBy.sector') },
    { key: 'difficultyLevel', label: t('groupBy.difficultyLevel') },
    { key: 'assignedSalespersonIds', label: t('groupBy.assignedSalespeople') },
    { key: 'maestro', label: t('groupBy.maestro') },
  ], [t]);

  const visibleOptions = availableKeys
    ? options.filter((o) => availableKeys.includes(o.key))
    : options;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
          value !== 'none'
            ? 'border-primary/40 bg-primary/10 text-primary'
            : 'border-border text-muted-foreground hover:border-primary/40 hover:text-primary'
        }`}
        title={t('groupBy.title')}
      >
        <ArrowDownUp className="h-4 w-4" />
        <span className="hidden sm:inline">{t('groupBy.buttonLabel')}</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-20 mt-1 w-56 rounded-lg border border-border bg-card py-1 shadow-lg animate-fade-in">
          {visibleOptions.map((opt) => (
            <button
              key={opt.key}
              onClick={() => {
                onChange(opt.key);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                value === opt.key
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <span className="w-4 shrink-0">
                {value === opt.key && <Check className="h-4 w-4" />}
              </span>
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
